import React, { useState } from 'react';
import { StyleSheet, View, Pressable } from 'react-native';
import { IThemeContext, useThemeContext } from '../../contexts';
import { Credential } from '../../types/credentials';
import { animated, useSprings } from '@react-spring/web';
import CredentialCard from './card/CredentialCard';

interface CredentialListProps {
  credentials: Credential[];
}

/**
 * @description Credential list component
 * @param {CredentialListProps} { credentials }
 * @return {React.FC}
 */
const CredentialList: React.FC<CredentialListProps> = ({ credentials }) => {
  const { theme } = useThemeContext();
  const styles = createCredentialListStyles(theme);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  // Calculate positions for each card
  const getCardPosition = (index: number) => {
    if (selectedIndex === null) {
      // Default stacked position
      return -115 * index;
    }

    if (index === selectedIndex) {
      // Selected card stays in place
      return -115 * index;
    }

    // Cards above selected move up, cards below move down
    const offset = 200; // Increased spacing when a card is selected
    return index < selectedIndex ? -115 * index - offset : -115 * index + offset;
  };

  // Create spring animations for all cards at once using useSprings
  const springs = useSprings(
    credentials.length,
    credentials.map((_, index) => ({
      to: {
        transform: `translateY(${getCardPosition(index)}px)`,
        scale: selectedIndex === index ? 1.05 : 1
      },
      config: {
        tension: 300,
        friction: 30
      }
    }))
  );

  return (
    <View style={styles.listContainer}>
      {springs.map((spring, index) => {
        return (
          <animated.div
            key={index}
            style={{
              ...spring,
              position: 'relative',
              cursor: 'pointer',
              width: '100%'
            }}>
            <Pressable
              onPress={() => {
                setSelectedIndex(selectedIndex === index ? null : index);
              }}>
              <CredentialCard credential={credentials[index]} />
            </Pressable>
          </animated.div>
        );
      })}
    </View>
  );
};

export default CredentialList;

const createCredentialListStyles = (
  theme: IThemeContext['theme']
): ReturnType<typeof StyleSheet.create> =>
  StyleSheet.create({
    container: {
      width: '100%',
      minHeight: '100%',
      maxWidth: 500,
      marginRight: 'auto',
      marginLeft: 'auto'
    },
    listContainer: {
      width: '100%',
      position: 'relative',
      paddingTop: 150,
      paddingBottom: 150,
      minHeight: '100%'
    },
    emptyListStyle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
      width: 350,
      gap: 24,
      height: '100%',
      padding: 0,
      margin: 'auto'
    },
    emptyListTextStyle: {
      display: 'flex',
      alignItems: 'center',
      gap: 12
    },
    emptyListMessageTitleStyle: {
      fontSize: 24,
      fontWeight: '700',
      textAlign: 'center'
    },
    emptyListMessageSubtitleStyle: {
      fontSize: 18,
      color: theme.textSecondary,
      textAlign: 'center'
    },
    onboardButton: {
      paddingVertical: 16,
      paddingHorizontal: 24,
      backgroundColor: theme.primary,
      borderRadius: 6,
      color: '#fff',
      textAlign: 'center',
      boxShadow: '1px 2px 4px rgba(41, 45, 50, 0.5)'
    },
    onboardButtonText: {
      textAlign: 'center',
      color: 'inherit',
      width: 350,
      fontSize: 16,
      fontWeight: '600'
    }
  });
